import { AspectRatio, Box, Grid, Skeleton, Image } from '@chakra-ui/react';
import { FC, useMemo, useState } from 'react';

import { WatchOnUnderline } from '@/components/ui/features/embed/components/WatchOnUnderline';
import { EmbedOverlayButton } from '@/components/ui/features/embed/EmbedOverlayButton';
import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import {
  useLectureVideoAnalytics,
  useTrackOptions,
} from '@/components/ui/features/lecture/LecturePlayer/LecturePlayer.hooks';
import { VideoJS } from '@/components/ui/shared/video/VideoJS/VideoJS';
import { defaultVideoType } from '@/constants/video';
import { queryThinLectureWithTagAndEvent } from '@/fetchers/thinLectures';
import { useResource, useResources } from '@/lib/datx-jsonapi-react';
import { queryLectureAccessPermission } from '@/queries/lecture-access-permissions';
import { queryTranscriptsByLectureId } from '@/queries/transcripts';

const FALLBACK_POSTER_PATH = '/images/landing/video-lectures.png';

export const EmbedVideo: FC = () => {
  const { id: lectureId } = useLecturePageQueryParams();
  const { data: lecture } = useResource(queryThinLectureWithTagAndEvent(lectureId));
  const { data: transcripts } = useResources(queryTranscriptsByLectureId(lectureId));
  const trackOptions = useTrackOptions({ transcripts, activeTrackId: null });
  const isLoading = !lecture || !transcripts;

  const { data } = useResource(queryLectureAccessPermission(lectureId));

  const isLectureRestricted = data?.restricted;

  const videoEvents = useLectureVideoAnalytics();
  const [isPlaying, setIsPlaying] = useState(false);

  const videoOptions = useMemo(
    () => ({
      sources: [{ src: lecture?.playlist, type: defaultVideoType }],
      tracks: trackOptions,
      poster: lecture?.largePosterUrl,
    }),
    [lecture?.playlist, lecture?.largePosterUrl, trackOptions]
  );

  if (isLoading) {
    return <Skeleton w="full" h={{ base: '300px', lg: '400px' }} />;
  }

  if (isLectureRestricted) {
    return (
      <Box position="relative">
        <Image
          w="full"
          h="100vh"
          objectFit="cover"
          opacity={0.3}
          src={lecture?.largePosterUrl || FALLBACK_POSTER_PATH}
          alt="Lecture placeholder background"
        />
        <Grid w="full" h="full" placeItems="center" position="absolute" top="0" left="0">
          <EmbedOverlayButton />
        </Grid>
      </Box>
    );
  }

  return (
    <AspectRatio ratio={16 / 9}>
      <Box>
        <VideoJS
          data-peer
          key={lectureId}
          title={lecture.title}
          options={videoOptions}
          underlineLink={`/lecture/${lectureId}-${lecture.slug}`}
          h="full"
          crossOrigin="anonymous"
          {...videoEvents}
          onPlay={(e) => {
            setIsPlaying(true);
            videoEvents.onPlay?.(e);
          }}
          onResume={(e) => {
            setIsPlaying(true);
            videoEvents.onResume?.(e);
          }}
          onPause={(e) => {
            setIsPlaying(false);
            videoEvents.onPause?.(e);
          }}
          onEnded={(e) => {
            setIsPlaying(false);
            videoEvents.onEnded?.(e);
          }}
        />
        {!isPlaying && <WatchOnUnderline lecture={lecture} />}
      </Box>
    </AspectRatio>
  );
};
